/**
 * ProfileDropdown — desktop auth area: loading skeleton, sign-in button,
 * or avatar trigger with the profile dropdown card.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, LogIn, LogOut, User, BadgeCheck, Mic2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useTheme } from '../../../context/ThemeContext';
import { useAuthModal } from '../../../context/AuthModalContext';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { useNavbarUser } from './useNavbarUser';
import UserAvatar from './UserAvatar';

export default function ProfileDropdown() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { theme } = useTheme();
  const isSukhasan = theme === 'sukhasan';
  const { openAuthModal } = useAuthModal();
  const {
    isAuthenticated,
    loading,
    displayName,
    photoURL,
    initials,
    email,
    isRagi,
    showClaimCta,
    claimCtaLabel,
    handleSignOut,
  } = useNavbarUser();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useClickOutside(dropdownRef, () => setOpen(false), open);
  useBodyScrollLock(open);

  if (loading) {
    /* Skeleton: matches avatar (w-7 h-7) + chevron (w-[13px]) + gaps */
    return (
      <div className="flex items-center gap-1.5 py-1 pl-1 pr-2">
        <div className="skeleton-shimmer w-7 h-7 rounded-full" />
        <div className="skeleton-shimmer w-3 h-3 rounded" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="animate-fadeIn">
        <button
          onClick={() => openAuthModal()}
          className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors text-accent-500 hover:bg-elevated"
        >
          <LogIn size={16} />
          Sign In
        </button>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      <div className="relative" ref={dropdownRef}>
        {/* Avatar trigger */}
        <button
          onClick={() => setOpen((prev) => !prev)}
          className="flex items-center gap-1.5 rounded-full py-1 pl-1 pr-2 transition-all focus:outline-none hover:bg-elevated"
          title={displayName}
        >
          <UserAvatar
            photoURL={photoURL}
            displayName={displayName}
            initials={initials}
            isSukhasan={isSukhasan}
            sizeClass="w-7 h-7"
            textClass="text-[10px]"
          />
          <ChevronDown
            size={13}
            className={cn(
              'transition-transform duration-200 text-tx-muted',
              open && 'rotate-180',
            )}
          />
        </button>

        {/* Profile dropdown with backdrop below navbar */}
        {open && (
          <>
            {/* Backdrop — covers below navbar only, navbar stays untouched */}
            <div
              className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm"
              style={{ top: '3.5rem' }}
              onClick={() => setOpen(false)}
            />
            {/* Dropdown card — anchored below avatar */}
            <div className="card-glass absolute right-0 top-full mt-2 z-[91] w-72 rounded-2xl p-0 shadow-2xl overflow-hidden animate-slideUp backdrop-blur-xl">
              {/* Top accent bar */}
              <div className="h-1.5 w-full bg-gradient-to-r from-accent-600 via-accent-500 to-accent-400" />

              {/* User info */}
              <div className="px-5 pt-5 pb-4">
                <div className="flex items-center gap-3">
                  <UserAvatar
                    photoURL={photoURL}
                    displayName={displayName}
                    initials={initials}
                    isSukhasan={isSukhasan}
                    sizeClass="w-12 h-12"
                    textClass="text-sm"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate text-tx-primary">
                      {displayName}
                    </p>
                    <p className="text-xs truncate text-tx-muted">
                      {email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px mx-5" style={{ backgroundColor: 'var(--glass-border)' }} />

              {/* Actions */}
              <div className="px-3 py-2 space-y-0.5">
                <Link
                  to="/profile"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-tx-secondary hover:bg-elevated"
                >
                  <User size={16} />
                  My Profile
                </Link>
                {isRagi && (
                  <Link
                    to="/profile/ragi"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors text-accent-500 hover:bg-elevated"
                  >
                    <Mic2 size={16} />
                    Edit Raagi profile
                  </Link>
                )}
                {showClaimCta && (
                  <Link
                    to="/claim"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors text-accent-500 hover:bg-elevated"
                  >
                    <BadgeCheck size={16} />
                    {claimCtaLabel}
                  </Link>
                )}
                <button
                  onClick={() => {
                    setOpen(false);
                    handleSignOut();
                  }}
                  className="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-[#EF4444] hover:bg-elevated"
                >
                  <LogOut size={16} />
                  Sign Out
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
